import BaseModel from "../models/BaseModel";
import BaseModelMap from "../models/BaseModelMap";
import BaseObject from "../models/BaseObject";
import {
  AddToCollectionOperation,
  ChangeObjectOperation,
  clonePersistentData,
  DeleteObjectOperation,
  NewObjectOperation,
  RemoveFromCollectionOperation,
  UndoOperation,
} from "./UndoOperation";

type UndoStepParams = {
  selectedObject?: BaseModel;
};

let debugIdCounter = 0;
export default class UndoStep {
  debugId = debugIdCounter++;
  readonly timestamp = Date.now();
  selectedObjectId: string | undefined;
  selectedObjectDescription: string | undefined;
  operations = Array<UndoOperation>();
  private entries = Array<UndoOperation>();
  private changedObjects = new Map<string, ChangeObjectOperation>();
  private deletedObjects = new Set<string>();
  private newObjects = new Set<string>();
  private explicitDescription: string | undefined;

  constructor(params?: UndoStepParams) {
    this.selectedObject = params?.selectedObject || state.selectionManager.selectedObject;
  }

  ////////////////////////////////////////////////////////////

  undo() {
    const restoredObjects = new Set<BaseObject>();
    for (let i = this.entries.length - 1; i >= 0; i--) {
      const entry = this.entries[i];
      entry.undo();
      if (entry.kind === "deleteObject") restoredObjects.add(entry.object);
    }
    this.restoreModelRelations(restoredObjects);
  }

  redo() {
    const restoredObjects = new Set<BaseObject>();
    for (const entry of this.entries) {
      entry.redo();
      if (entry.kind === "newObject") restoredObjects.add(entry.object);
    }
    this.restoreModelRelations(restoredObjects);
  }

  /** Restores derived relations only after all objects, snapshots and collections have been replayed. */
  private restoreModelRelations(objects: ReadonlySet<BaseObject>): void {
    for (const object of objects) {
      if (!object.isDeleted) object.undelete();
    }
  }

  ////////////////////////////////////////////////////////////

  get selectedObject(): BaseModel | undefined {
    return state.objectRegistry.getObject<BaseModel>(this.selectedObjectId || "");
  }

  set selectedObject(object: BaseModel | undefined) {
    if (object) {
      this.selectedObjectId = object.id;
      this.selectedObjectDescription = `${object.fullTag} ${object.name}`.trim();
    }
  }

  ////////////////////////////////////////////////////////////

  get isEmpty(): boolean {
    return this.entries.length === 0;
  }

  get description(): string {
    if (this.explicitDescription) return this.explicitDescription;

    const objId = this.selectedObjectId;
    let operation: UndoOperation | undefined;
    let objectDescription = this.selectedObjectDescription;

    if (objId) {
      for (let i = this.operations.length - 1; i >= 0; i--) {
        if (this.operations[i].objectId === objId) {
          operation = this.operations[i];
          break;
        }
      }
    }

    if (!operation) {
      operation = this.operations[0];
      if (operation) {
        objectDescription = `${operation.object.fullTag} ${operation.object.name}`.trim();
      }
    }

    if (!operation) {
      return "Änderung";
    }

    const action = this.getOperationDescription(operation);
    if (!action) {
      return `unknown Action: ${objectDescription}`.trim();
    }

    return `${action}: ${objectDescription}`.trim();
  }

  set description(value: string) {
    this.explicitDescription = value.trim() || undefined;
  }

  getDebugInfo() {
    return this.operations.map((operation, operationIndex) => {
      const internalOperation = operation as any;
      const object = internalOperation.object;
      const collection = internalOperation.collection;
      return {
        operation: operationIndex + 1,
        kind: operation.kind,
        objectClass: object?.className,
        objectId: operation.objectId,
        objectTag: object?.tag,
        objectName: object?.name,
        collectionId: internalOperation.collectionId,
        collectionProperty: collection?.propertyId,
        collectionParent: collection?.parent?.className,
      };
    });
  }

  captureObject(object: BaseObject) {
    if (this.isTemporaryObject(object)) return;
    if (this.newObjects.has(object.id) || this.changedObjects.has(object.id)) return;

    const operation = new ChangeObjectOperation(object, clonePersistentData(object.createUndoSnapshot()));
    this.changedObjects.set(object.id, operation);
    this.addOperation(operation);
  }

  captureNewObject(object: BaseModel) {
    if (object.isTemporary) return;
    if (this.newObjects.has(object.id)) return;
    const changeOperation = this.changedObjects.get(object.id);
    if (changeOperation) {
      this.changedObjects.delete(object.id);
      this.operations = this.operations.filter((operation) => operation !== changeOperation);
      this.entries = this.entries.filter((entry) => entry !== changeOperation);
    }
    this.newObjects.add(object.id);
    this.addOperation(new NewObjectOperation(object));
  }

  captureDeletedObject(object: BaseModel) {
    if (object.isTemporary) return;
    if (this.newObjects.has(object.id) || this.deletedObjects.has(object.id)) return;
    this.deletedObjects.add(object.id);
    const snapshot = clonePersistentData(object.toPersistentData());
    this.addOperation(new DeleteObjectOperation(object, snapshot));
  }

  captureAddToCollection(collection: BaseModelMap<BaseModel>, object: BaseModel, index: number) {
    this.addOperation(new AddToCollectionOperation(collection, object, index));
  }

  captureRemoveFromCollection(collection: BaseModelMap<BaseModel>, object: BaseModel, index: number) {
    this.addOperation(new RemoveFromCollectionOperation(collection, object, index));
  }

  private addOperation(operation: UndoOperation) {
    if (this.isTemporaryObject(operation.object)) return;

    if (!this.selectedObject) {
      this.selectedObjectId = state.selectionManager.selectedObject?.id;
    }

    this.operations.push(operation);
    this.entries.push(operation);
  }

  filterTemporaryObjects() {
    const shouldKeep = (operation: UndoOperation) => {
      if (this.isTemporaryObject(operation.object)) return false;

      if (operation.kind === "newObject") {
        const object = (operation as NewObjectOperation).object;
        if (!object.parent) return false;
      }

      return true;
    };

    this.operations = this.operations.filter(shouldKeep);
    this.entries = this.entries.filter(shouldKeep);

    const retainedObjectIds = new Set(this.entries.map((entry) => entry.objectId).filter(Boolean));
    for (const objectId of this.newObjects) {
      if (!retainedObjectIds.has(objectId)) this.newObjects.delete(objectId);
    }
    for (const objectId of this.deletedObjects) {
      if (!retainedObjectIds.has(objectId)) this.deletedObjects.delete(objectId);
    }
    for (const objectId of this.changedObjects.keys()) {
      if (!retainedObjectIds.has(objectId)) this.changedObjects.delete(objectId);
    }
  }

  private isTemporaryObject(object: BaseObject): boolean {
    return object.isBaseModel && Boolean((object as BaseModel).isTemporary);
  }

  private getOperationDescription(operation: UndoOperation): string {
    switch (operation.kind) {
      case "deleteObject":
        return "Löschen";

      case "removeFromCollection":
        return "Entfernen";

      case "changeObject":
        return "Ändern";

      case "newObject":
        return "Neu anlegen";

      case "addToCollection":
        return "Einfügen";

      default:
        return "";
    }
  }
}
